import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Image as ImageIcon, Trash, Crown } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export async function fetchLogo() {
  try {
    const r = await api.get("/settings/logo", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
    canvas.getContext("2d").drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    return { dataUrl: canvas.toDataURL("image/png"), width: img.naturalWidth, height: img.naturalHeight };
  } catch {
    return null;
  }
}

export function LogoUploader({ hasLogo, onChange }) {
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const allowed = user?.billing?.is_owner || user?.billing?.plan !== "essencial";

  useEffect(() => { if (hasLogo) fetchLogo().then((l) => setPreview(l?.dataUrl || null)); else setPreview(null); }, [hasLogo]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo atualizada!");
      onChange?.(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async () => {
    try {
      await api.delete("/settings/logo");
      toast.success("Logo removida");
      onChange?.(false);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="logo-uploader">
      <Label>Logo da clínica (aparece no orçamento em PDF)</Label>
      {!allowed ? (
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-2" data-testid="logo-upgrade-notice">
          <Crown size={18} className="mt-0.5 shrink-0" />
          <span>Disponível nos planos Profissional e Clínica. <Link to="/app/billing" className="underline font-semibold">Fazer upgrade</Link></span>
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-4 flex-wrap">
          <div className="w-40 h-20 rounded-md border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
            {preview ? <img src={preview} alt="Logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview" /> : <ImageIcon size={28} className="text-slate-400" />}
          </div>
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid="logo-file-input" />
          <Button type="button" variant="outline" disabled={busy} onClick={() => ref.current?.click()} data-testid="logo-upload-btn">{busy ? "Enviando..." : hasLogo ? "Trocar logo" : "Enviar logo"}</Button>
          {hasLogo && <Button type="button" variant="ghost" onClick={remove} className="text-rose-600" data-testid="logo-remove-btn"><Trash size={16} className="mr-1" /> Remover</Button>}
          <p className="w-full text-xs text-slate-500">PNG, JPG ou WebP até 2MB. Fundo transparente fica melhor.</p>
        </div>
      )}
    </div>
  );
}
